import type { WhatsAppInboxItem } from '../types';

const REQUEST_LABEL = { book: 'Asked to book', call: 'Asked for a call' } as const;

function age(value: string | null) {
  if (!value) return '';
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export default function WhatsAppNeedsReply({
  items,
  pendingCount,
  onSelect,
}: {
  items: WhatsAppInboxItem[];
  pendingCount: number;
  onSelect: (leadId: string) => void;
}) {
  return (
    <section className="absolute left-2 right-2 top-12 z-20 max-h-[70vh] overflow-y-auto rounded-2xl border border-emerald-200 bg-white p-3 shadow-xl">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">WhatsApp · Needs Reply ({pendingCount})</h2>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Book / call requests first, then oldest</span>
      </div>
      {items.length === 0 ? (
        <p className="rounded-xl bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">No patients waiting for a reply.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => item.current_lead_id && onSelect(item.current_lead_id)}
              className="w-full rounded-xl border border-slate-200 p-3 text-left hover:border-emerald-300 hover:bg-emerald-50"
              disabled={!item.current_lead_id}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="truncate text-sm font-semibold text-slate-900">{item.name || item.phone_identity}</span>
                <span className="shrink-0 text-xs text-slate-400">{age(item.first_unanswered_at)}</span>
              </div>
              {item.patient_request && (
                <span
                  className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    item.patient_request_waiting ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {REQUEST_LABEL[item.patient_request]}{item.patient_request_waiting ? ' · waiting for a reply' : ''}
                </span>
              )}
              <p className="mt-1 line-clamp-2 text-xs text-slate-600">{item.last_inbound_preview || 'New WhatsApp message'}</p>
              <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                <span className="rounded-full bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">{item.unread_count} unread</span>
                {item.assigned_to && <span className="truncate">{item.assigned_to}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
